import { getTestImpactScorelementId } from '@upright/test-ids/dom-elements';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { scaleLinear, ScaleLinear } from 'd3-scale';
import { compact, maxBy, sum } from 'lodash';
import { ReactNode, RefObject } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import type { Leena2Interval } from '../../../../../../../../../common/model/output/scores/leena2-intervals';
import { ImpactScoresByTIVV } from '../../../../../../../../../common/model/output/scores/types';
import { allValueChains, Dimension, Valence, ValueChain } from '../../../../../../../../../common/model/types';
import { BouColor } from '../../../../../../../generic/global/bou-colors';
import { seriesColorScale } from '../../../../../../../generic/global/colors';
import { valenceColor } from '../../../../../../../generic/utilities/format';
import { useImpactChartContext, useImpactChartFontSizes, useImpactChartWidths } from '../../context';
import { getValueChainFill } from '../../value-chain-fill';
import { formatImpactScore } from '../../value-formatter';
import { RectWithFillPattern } from './rect-with-fill-pattern';

interface Props {
  data: ImpactScoresByTIVV | undefined;
  index: number;
  impactCode?: string;
  dimension?: Dimension;
  valence: Valence;
  maxWidth: number;
  height: number;
  noHover?: boolean;
  getUrl?: (valueChain: ValueChain | undefined) => string;
  onClick: (valueChain: ValueChain | undefined) => void;
  svgId: string;
  color?: string;
  tooltipRef?: RefObject<SVGGElement>;
  /** Leena-2 Clef results with "Show Leena-2 uncertainty" on: the bar value's
   *  80% interval, in the same unit as the bar (already scaled by the score
   *  reader). Drawn as a thin p10–p90 whisker over the bar. */
  interval?: Leena2Interval;
}

interface Part {
  valueChain: ValueChain | undefined;
  value: number;
}

export function StackedBar(props: Props) {
  const widths = useImpactChartWidths();
  const ctx = useImpactChartContext();
  const fontSizes = useImpactChartFontSizes();

  const { scoreType, singleSeries, maxX, formatters, simplify, scoreReaders } = ctx;
  const scoreReader = scoreReaders[props.index];

  const scale = scaleLinear().domain([0, maxX]).range([0, widths.barRange]);
  const scoreValue = scoreReader.readScore(props.impactCode, props.dimension, undefined, props.valence);
  // Ghost baseline (see `ImpactChartDataSeries.ghostScoresByTIVV`): where it
  // exceeds this bar's own score, the difference is appended after the solid
  // bar as a faded striped tail. A bar can be ghost-only (score filtered all
  // the way to zero), so it must still render when only the ghost has value.
  const ghostReader = ctx.ghostScoreReaders[props.index];
  const ghostScoreValue = ghostReader?.readScore(props.impactCode, props.dimension, undefined, props.valence) ?? 0;
  if (!scoreValue && !ghostScoreValue) {
    return <g />;
  }
  const solidValue = scoreValue ?? 0;

  const getScoreForValueChain = (valueChain: ValueChain | undefined) => {
    // Read score part or zero
    const value = scoreReader.readScore(props.impactCode, props.dimension, valueChain, props.valence) || 0;
    return { value, valueChain };
  };

  const parts = getAggregatedParts(getScoreForValueChain, ctx.showValueChains, scale);

  const valence = props.valence;
  let totalBarWidth = 0;
  const barHeight = props.height;
  const sign = valence === Valence.POSITIVE ? 1 : -1;
  const textSpacing = 10;
  const textAnchor = valence === Valence.POSITIVE ? 'start' : 'end';

  const fill = props.color || getFillColor(singleSeries, valence, !!props.dimension, props.index);

  // Per-series click opt-out (`ImpactChartDataSeries.clickDisabled`): kill
  // the pointer cursor and hover effect so a non-clickable series doesn't
  // advertise a drilldown it can't deliver. The actual no-op click handling
  // lives in the impact-row bar wrapper.
  const seriesClickDisabled = ctx.data[props.index].clickDisabled === true;
  const noHover = props.noHover || seriesClickDisabled;

  // Ghost tail geometry. The 2px gap mirrors the stacked parts' spacing; the
  // 1px minimum swallows float noise between the baseline and the series'
  // own (recomputed) scores so unaffected bars never grow phantom tails.
  const solidTotalWidth = scale(solidValue) ?? 0;
  const ghostTotalWidth = scale(ghostScoreValue) ?? 0;
  const ghostGap = solidTotalWidth > 0 ? 2 : 0;
  const ghostTailWidth = ghostTotalWidth - solidTotalWidth - ghostGap;
  const hasGhostTail = ghostTailWidth >= 1;
  // The stripe pattern def lives inline in this bar's group; the id is unique
  // per svg (svgId) / row / valence / series, so sibling bars can't collide.
  const ghostPatternId = `ghost-stripes-${props.svgId}-${props.dimension ?? props.impactCode ?? ''}-${valence}-${props.index}`;

  // Uncertainty whisker (p10–p90), clamped to the bar range so a long upper
  // tail can't run into the value label column; a clamped end gets no cap.
  const whisker = props.interval
    ? {
        from: Math.max(0, Math.min(widths.barRange, scale(props.interval.p10) ?? 0)),
        to: Math.max(0, Math.min(widths.barRange, scale(props.interval.p90) ?? 0)),
        clipped: (scale(props.interval.p90) ?? 0) > widths.barRange,
      }
    : undefined;
  const whiskerCapHalf = barHeight * 0.3;

  const fontSize = fontSizes.impactScores;

  const testId = getTestImpactScorelementId(
    props.dimension ? props.dimension : (props.impactCode as ImpactCategoryId),
    props.valence,
  );
  return (
    <Root
      ref={props.tooltipRef}
      style={ctx.disableImpactClick || seriesClickDisabled ? { cursor: 'default' } : { cursor: 'pointer' }}
    >
      {parts.map((part, index) => {
        const barWidth = scale(part.value) ?? 0;
        totalBarWidth += barWidth;
        const x = valence === Valence.POSITIVE ? totalBarWidth - barWidth : -totalBarWidth;

        const valueChain = part.valueChain;
        const fillPattern = getValueChainFill(valueChain);

        const stackSpacing = 2;
        const isLast = index === parts.length - 1;
        const adjustedBarWidth = isLast ? barWidth : barWidth - stackSpacing;
        const adjustedX = !isLast && valence === Valence.NEGATIVE ? x + stackSpacing : x;

        if (adjustedBarWidth < 0) {
          return undefined;
        }

        return (
          <LinkWrapperMaybe key={index} getUrl={props.getUrl} valueChain={valueChain}>
            <RectWithFillPattern
              x={adjustedX}
              y={-barHeight / 2}
              width={adjustedBarWidth}
              height={barHeight}
              fill={fill}
              hoverFill={noHover ? fill : undefined}
              activeFill={noHover ? fill : undefined}
              fillPattern={fillPattern}
              rx={2}
              ry={2}
              onClick={() => {
                props.onClick(valueChain);
              }}
            />
          </LinkWrapperMaybe>
        );
      })}
      {hasGhostTail && (
        <g>
          <defs>
            <pattern
              id={ghostPatternId}
              patternUnits="userSpaceOnUse"
              width={6}
              height={6}
              patternTransform="rotate(45)"
            >
              <rect width={3} height={6} fill={fill} />
            </pattern>
          </defs>
          <rect
            x={
              valence === Valence.POSITIVE ? solidTotalWidth + ghostGap : -(solidTotalWidth + ghostGap + ghostTailWidth)
            }
            y={-barHeight / 2}
            width={ghostTailWidth}
            height={barHeight}
            fill={`url(#${ghostPatternId})`}
            // Faded on purpose: the tail is context, not a score — it should
            // read as a shadow of the solid bar, not compete with it.
            opacity={0.45}
            rx={2}
            ry={2}
            onClick={() => {
              props.onClick(undefined);
            }}
          />
        </g>
      )}
      {whisker && (
        <Whisker>
          <line x1={whisker.from * sign} x2={whisker.to * sign} y1={0} y2={0} />
          <line x1={whisker.from * sign} x2={whisker.from * sign} y1={-whiskerCapHalf} y2={whiskerCapHalf} />
          {!whisker.clipped && (
            <line x1={whisker.to * sign} x2={whisker.to * sign} y1={-whiskerCapHalf} y2={whiskerCapHalf} />
          )}
        </Whisker>
      )}
      {simplify ? undefined : (
        <g>
          <Text
            onClick={() => {
              props.onClick(undefined);
            }}
            id={testId}
            x={(Math.max(totalBarWidth, hasGhostTail ? ghostTotalWidth : 0, whisker?.to ?? 0) + textSpacing) * sign}
            dy="0.35em"
            textAnchor={textAnchor}
            style={{ fontSize }}
          >
            {valence === Valence.POSITIVE ? '+' : '-'}
            {formatImpactScore(solidValue, formatters, scoreType)}
          </Text>
        </g>
      )}
    </Root>
  );
}

function LinkWrapperMaybe(props: {
  getUrl: ((valueChain: ValueChain | undefined) => string) | undefined;
  valueChain: ValueChain | undefined;
  children: ReactNode;
}) {
  if (props.getUrl) {
    return <Link to={props.getUrl(props.valueChain)}>{props.children}</Link>;
  }
  return <>{props.children}</>;
}

/**
 * Get aggregated bar parts, aggregated in such a way that
 * bars that would be smaller than 5 pixels are aggregated
 * into the largest bar
 *
 * This is a slight misrepresentation but makes the output
 * much more cleaner by hiding unnecessary details
 */
function getAggregatedParts(
  getScoreForValueChain: (vc: ValueChain | undefined) => Part,
  showValueChains: boolean,
  scale: ScaleLinear<number, number>,
) {
  const parts = showValueChains ? allValueChains.map(getScoreForValueChain) : [getScoreForValueChain(undefined)];
  const largest = maxBy(parts, p => p.value);
  if (!largest) throw Error('Largest missing');
  const partShouldBeJoinedToLargest = (p: Part) => (scale(p.value) ?? 0) < 5 && p.valueChain !== largest.valueChain;

  const toBeJoined = parts.filter(partShouldBeJoinedToLargest);
  const totalToBeJoined = sum(toBeJoined.map(p => p.value));
  return compact(
    parts.map(p => {
      if (p.valueChain === largest.valueChain) {
        return {
          valueChain: p.valueChain,
          value: p.value + totalToBeJoined,
        };
      }
      if (partShouldBeJoinedToLargest(p)) {
        return undefined;
      }
      return p;
    }),
  );
}

function getFillColor(single: boolean, valence: Valence, dimension: boolean, index: number) {
  if (!single) {
    return seriesColorScale(index);
  }
  return dimension ? valenceColor(valence) : BouColor.IMPACT_GENERIC_GRAY;
}

const Text = styled.text`
  fill: ${BouColor.IMPACT_SCORE_TEXT_BLACK};
`;

// Thin and slightly translucent: the whisker qualifies the bar, it shouldn't
// compete with it. Not interactive, so hovering and clicking hit the bar.
const Whisker = styled.g`
  stroke: var(--color__gray-800, #1e2938);
  stroke-width: 1;
  stroke-linecap: round;
  opacity: 0.75;
  pointer-events: none;
`;

const Root = styled.g`
  cursor: pointer;
`;
